import type BigNumber from 'bignumber.js';

import type { BittensorNativeAlphaBalance, GetBittensorNativeBalancesOutput } from 'clients/api';
import type { Token, TokenBalance } from 'types';
import type { Address, Hex } from 'viem';
import { getMarketCapabilities } from '../shared/getMarketCapabilities';
import type { VTokenWrapperKind } from '../shared/getVTokenWrapperKind';

export type BittensorNativeSupplySource = {
  kind: 'nativeAlpha';
  token: Token;
  wrappedToken: Token;
  wrapperAddress: Address;
  netuid: bigint;
  hotkey: Hex;
  exchangeRate: bigint;
  balanceMantissa: BigNumber;
  stakingAllowanceMantissa: BigNumber;
};

export interface GetBittensorSupplySourceTokensInput {
  underlyingToken: Token;
  wrapperKind: VTokenWrapperKind;
  bittensorNativeBalances?: GetBittensorNativeBalancesOutput;
}

export interface GetBittensorSupplySourceTokensOutput {
  tokenBalances: TokenBalance[];
  nativeSupplySources: BittensorNativeSupplySource[];
}

const getSyntheticNativeAlphaTokenAddress = (netuid: bigint): Address =>
  `0x${netuid.toString(16).padStart(40, '0')}` as Address;

const getNativeAlphaToken = ({
  netuid,
  wrapperToken,
}: {
  netuid: bigint;
  wrapperToken: Token;
}): Token => ({
  chainId: wrapperToken.chainId,
  address: getSyntheticNativeAlphaTokenAddress(netuid),
  decimals: 9,
  iconSrc: wrapperToken.iconSrc,
  isNative: true,
  symbol: `alpha-${netuid.toString()}`,
  displaySymbol: wrapperToken.displaySymbol,
});

const mapNativeAlphaSource = (
  alphaBalance: BittensorNativeAlphaBalance,
): BittensorNativeSupplySource => {
  const token = getNativeAlphaToken({
    netuid: alphaBalance.netuid,
    wrapperToken: alphaBalance.wrapperToken,
  });

  return {
    kind: 'nativeAlpha',
    token,
    wrappedToken: alphaBalance.wrapperToken,
    wrapperAddress: alphaBalance.wrapperAddress,
    netuid: alphaBalance.netuid,
    hotkey: alphaBalance.hotkey,
    exchangeRate: alphaBalance.exchangeRate,
    balanceMantissa: alphaBalance.balanceMantissa,
    stakingAllowanceMantissa: alphaBalance.stakingAllowanceMantissa,
  };
};

export const getBittensorSupplySourceTokens = ({
  underlyingToken,
  wrapperKind,
  bittensorNativeBalances,
}: GetBittensorSupplySourceTokensInput): GetBittensorSupplySourceTokensOutput => {
  const capabilities = getMarketCapabilities({
    underlyingToken,
    wrapperKind,
    bittensorNativeBalances,
  });

  if (capabilities.canUseNativeTao && underlyingToken.tokenWrapped && bittensorNativeBalances) {
    return {
      tokenBalances: [
        {
          token: underlyingToken.tokenWrapped,
          balanceMantissa: bittensorNativeBalances.nativeTaoBalanceMantissa,
        },
      ],
      nativeSupplySources: [],
    };
  }

  const matchingAlphaBalance = bittensorNativeBalances?.alphaBalances.find(
    (alphaBalance) =>
      capabilities.canUseNativeAlpha &&
      alphaBalance.wrapperAddress.toLowerCase() === underlyingToken.address.toLowerCase(),
  );

  if (!matchingAlphaBalance) {
    return {
      tokenBalances: [],
      nativeSupplySources: [],
    };
  }

  const nativeAlphaSource = mapNativeAlphaSource(matchingAlphaBalance);

  return {
    tokenBalances: [
      {
        token: nativeAlphaSource.token,
        balanceMantissa: nativeAlphaSource.balanceMantissa,
      },
    ],
    nativeSupplySources: [nativeAlphaSource],
  };
};
